import { useSnackbar } from "notistack";
import React from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient, UseQueryOptions } from "react-query";
import {
  EquipmentAddDto,
  EquipmentDetailsDto,
  EquipmentEditDto,
  EquipmentTypeAddDto,
  EquipmentTypeListDto,
  useEquipmentApi,
} from "~/api/GeneratedApi";
import { useApiErrorHandler } from "~/utils/hooks/useApiErrorHandler";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import { EquipmentTypes } from "../components/EquipmentDialog";

export const getEquipmentClass = (
  equipment: EquipmentDetailsDto | undefined,
): EquipmentTypes | "" => {
  if (equipment?.vehicleItem) return "vehicle";
  if (equipment?.waterCraftItem) return "waterCraft";
  if (equipment?.workEquipmentItem) return "workEquipment";
  return "";
};

export const useEquipments = () => {
  const { getEquipment } = useEquipmentApi();

  const query = useCustomQuery("equipments", () => {
    return getEquipment();
  });

  const getEquipmentById = React.useCallback(
    (id: number) => {
      return query.data?.results.find((e) => e.id === id);
    },
    [query.data?.results],
  );

  return { ...query, getEquipmentById };
};

export const useEquipmentTypeRemove = () => {
  const { enqueueSnackbar } = useSnackbar();
  const qc = useQueryClient();
  const apiErrorHandler = useApiErrorHandler();
  const { t } = useTranslation();
  const { removeEquipmentType } = useEquipmentApi();

  const mutation = useMutation({
    mutationFn: (id: number) => {
      return removeEquipmentType(id);
    },
    onSuccess: () => {
      enqueueSnackbar(t("apiResponses:itemDeleted"), {
        variant: "success",
      });
      void qc.invalidateQueries("equipment-types");
    },
    onError: apiErrorHandler,
  });
  return mutation;
};

export const useEquipmentsTypes = (
  props: UseQueryOptions<EquipmentTypeListDto[]> = {},
) => {
  const { getEquipmentTypes } = useEquipmentApi();

  const query = useCustomQuery(
    "equipment-types",
    () => {
      return getEquipmentTypes();
    },
    props,
  );

  return { ...query, getEquipmentTypes };
};

export const useEquipmentById = (id: number | undefined | null) => {
  const { data } = useEquipments();
  if (!id) return undefined;
  const equipment = data?.results.find((r) => r.id === id);

  return equipment;
};

export const useVehicles = () => {
  const { getVehicles } = useEquipmentApi();

  const { data: vehicles, refetch } = useCustomQuery("vehicles", () => {
    return getVehicles();
  });

  return { vehicles, getVehicles, refetch };
};

export const useWaterCrafts = () => {
  const { getWaterCrafts } = useEquipmentApi();

  const query = useCustomQuery("waterCrafts", () => {
    return getWaterCrafts();
  });

  return { ...query, getWaterCrafts };
};

export const useWorkEquipments = () => {
  const { getWorkEquipment } = useEquipmentApi();

  const query = useCustomQuery("workEquipments", () => {
    return getWorkEquipment();
  });

  return { ...query, getWorkEquipment };
};

export const useAddEquipment = () => {
  const { addEquipment } = useEquipmentApi();
  // const apiErrorHandler = useApiErrorHandler();
  // const { t } = useTranslation();
  // const { enqueueSnackbar } = useSnackbar();
  const mutation = useMutation({
    mutationFn: (equipmentAdd: EquipmentAddDto) => {
      return addEquipment(equipmentAdd);
    },
    // onSuccess: () => {
    //   enqueueSnackbar(t("apiResponses:itemAdded"), {
    //     variant: "success",
    //   });
    // },
    // onError: () => {
    //   enqueueSnackbar(apiErrorHandler);
    // },
  });

  return mutation;
};

export const useEditEquipment = () => {
  const { editEquipment } = useEquipmentApi();
  // const apiErrorHandler = useApiErrorHandler();
  // const { t } = useTranslation();
  // const { enqueueSnackbar } = useSnackbar();
  const mutation = useMutation({
    mutationFn: ({
      idPath,
      ...rest
    }: EquipmentEditDto & { idPath: string }) => {
      return editEquipment(idPath as unknown as number, rest);
    },
    // onSuccess: () => {
    //   enqueueSnackbar(t("apiResponses:itemEdited"), {
    //     variant: "success",
    //   });
    // },
    // onError: () => {
    //   enqueueSnackbar(apiErrorHandler);
    // },
  });

  return mutation;
};

export const useAddEquipmentType = () => {
  const { addEquipmentType } = useEquipmentApi();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const mutation = useMutation({
    mutationFn: (equipmentAdd: EquipmentTypeAddDto) => {
      return addEquipmentType(equipmentAdd);
    },
    onSuccess: () => {
      enqueueSnackbar(t("apiResponses:itemAdded"), { variant: "success" });
    },
  });

  return mutation;
};
